import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { FC, useId } from "react";
import { APP_BREAKPOINT } from "src/constants/layout";
import { IconInfoCircle } from "src/constants/icons";
import LoaderIconImage from "src/ui/components/BlockingLoadingOverlay/LoadingIconSmall.webp";
import Translate from "src/ui/components/Translate/Translate";
import { Variant } from "./NotificationCardCubit";

export type NotificationCardDevice = "mobile" | "desktop";

const Card = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 16px;
  overflow: hidden;

  --card-bg: var(--color-cream-darker);
  --card-base: transparent;
  --card-border: rgba(170, 167, 160, 0.5);
  --card-text: inherit;

  &[data-variant="waitingForHIE"],
  &[data-variant="waitingForHIENonContinuation"] {
    --card-bg: rgba(95, 31, 143, 0.12);
    --card-base: var(--Greys-White, #fff);
    --card-border: rgba(95, 31, 143, 0.24);
    --card-text: var(--Accessible-PurpleHaze, #5f1f8f);
  }

  background:
    linear-gradient(90deg, var(--card-bg) 0%, var(--card-bg) 100%),
    var(--card-base);
  border: 1px solid var(--card-border);
  color: var(--card-text);

  @media (min-width: ${APP_BREAKPOINT}px) {
    padding: 20px 24px;
  }

  &[data-device="mobile"] {
    padding: 12px 16px;
  }

  &[data-device="desktop"] {
    padding: 20px 24px;
  }
`;

const SpinKeyframes = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Spinner = styled.img`
  width: 17.5px;
  height: 17.5px;
  animation: ${SpinKeyframes} 1.2s linear infinite;
  flex-shrink: 0;
`;

const IconWrap = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Content = styled.div`
  flex: 1 0 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  width: 100%;
`;

const cardTextBase = `
  margin: 0;
  font-family: "STK Bureau";
  font-size: 16px;
  font-style: normal;
  line-height: 22px;
  letter-spacing: -0.32px;
  font-feature-settings: 'liga' off, 'clig' off;
  word-break: break-word;
`;

const Title = styled.p`
  ${cardTextBase}
  flex: 1 0 0;
  min-width: 0;
  font-weight: 500;
`;

const Description = styled.p`
  ${cardTextBase}
  width: 100%;
  font-weight: 400;
`;

const ImageContainer = styled.div`
  width: 4rem;
  height: 4em;
  margin-right: -1rem;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &[data-custom="true"] {
    width: 6rem;
    height: 6em;
    margin: -1.75rem calc(-2rem - 10px) 0 0;
  }
`;

const VariantImage: FC<{
  variantImage: string;
  variantImageIsWide: boolean;
}> = ({ variantImage, variantImageIsWide }) => {
  return (
    <ImageContainer data-custom={variantImageIsWide}>
      <img src={variantImage} alt="" aria-hidden="true" />
    </ImageContainer>
  );
};

export interface NotificationCardViewProps {
  variant: Variant;
  variantImage: string | null;
  variantImageIsWide: boolean;
  /** Omit to follow the viewport. Only stories pin a device to show both side by side. */
  device?: NotificationCardDevice;
}

const NotificationCardView: FC<NotificationCardViewProps> = ({
  variant,
  variantImage,
  variantImageIsWide,
  device
}) => {
  const id = useId();
  const titleId = `${id}-title`;
  const descriptionId = `${id}-description`;

  const isRecordLookupVariant =
    variant === Variant.waitingForHIE ||
    variant === Variant.waitingForHIENonContinuation;

  return (
    <Card
      role="article"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      data-variant={variant}
      data-device={device}
    >
      <Content>
        <Header>
          <IconWrap aria-hidden="true">
            {isRecordLookupVariant ? (
              <Spinner src={LoaderIconImage} alt="" />
            ) : (
              <IconInfoCircle />
            )}
          </IconWrap>
          <Title id={titleId}>
            <Translate
              msg="notification.card.title"
              variables={{ context: variant }}
            />
          </Title>
        </Header>
        <Description id={descriptionId}>
          <Translate
            msg="notification.card.description"
            variables={{ context: variant }}
          />
        </Description>
      </Content>
      {variantImage && (
        <VariantImage
          variantImage={variantImage}
          variantImageIsWide={variantImageIsWide}
        />
      )}
    </Card>
  );
};

export default NotificationCardView;
